// IMPORT PACKAGE REFERENCES
import React, { useState, useCallback, memo } from "react";
import { CustomCardContent } from "../card-content/CardContent";
import { SimpleCardHeader } from "../card/card-header/CardHeader";
import {
  Grid,
  Chip,
  List,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
  Checkbox,
  Button,
  ButtonGroup,
  Typography,
  Box
} from "@material-ui/core";
import styles from "./ListableRulesCard.module.scss"; // Import css modules stylesheet as styles
import { Loading } from "../loading/Loading";
import Toolbar from "../toolbar/Toolbar";
import { getIcon } from "../../icon/icon";
import ListableRulesItem, { ListableRulesCardDataModel } from "./listable-item-rules/ListableRulesItem";
import ShrinkingButtonGroup from "../shrinking-button-group/ShrinkingButtonGroup";
import { withTranslation } from "react-i18next";
import { FixMeLater } from "types/FixMeLaterType";
import { useTranslation } from 'react-i18next'
import { setSeconds } from "date-fns";
import { Option } from "components/textbox/Textbox";

interface ListableRulesCardProps {
  // Fields
  disabled: boolean,
  data: ListableRulesCardDataModel[],
  isLoading: boolean,
  emptyPlaceholder: FixMeLater,
  modelType: string,
  actionButton: FixMeLater,
  subheading: string,
  title: string,

  // Functions
  handleDelete: FixMeLater, // probably function as type.
  addLabel: FixMeLater
}

function ListableRulesCard(props: ListableRulesCardProps) {
  // Page Utilities
  const [t, i18n] = useTranslation()

  // Page state controls
  const [selectedItems, setSelectedItems] = useState({});
  const [toolbarShowing, setToolbarShowing] = useState(!props.disabled)

  const handleCheck = (id: string) => {
    const localSelectedItems: FixMeLater = Object.assign({}, selectedItems);

    if (!selectedItems.hasOwnProperty(id)) {
      localSelectedItems[id] = true;
    } else {
      delete localSelectedItems[id];
    }

    setSelectedItems(localSelectedItems);
  }

  const multiSelect = useCallback((shouldDeselect: boolean) => {
    const localSelectedItems: FixMeLater = {};

    if (shouldDeselect) {
      setSelectedItems(localSelectedItems);
      return;
    }

    props.data.forEach((data: ListableRulesCardDataModel) => {
      let id = "";

      id = data.id ? "" : data.id;

      localSelectedItems[id] = true;
    });

    setSelectedItems(localSelectedItems);
  }, [props.data, setSelectedItems])

  const toggleToolbar = () => {
    setToolbarShowing(!props.disabled);
  }

  const getDataList = () => {
    if (props.isLoading) {
      return <Loading />;
    }

    if (props?.data?.length === 0) {
      return (
        <Box className={styles.placeholder}>{props.emptyPlaceholder}</Box>
      );
    }

    const allSelected =
      Object.keys(selectedItems).length === props.data?.length;

    return (
      <Grid>
        <List>
          {(!props.disabled && (props.data?.length > 0)) && (
            <Toolbar
              showDelete={Object.keys(selectedItems).length > 0}
              onDelete={() => {
                props.handleDelete(Object.keys(selectedItems));
                setSelectedItems({});
              }}
              multiSelectLabel={allSelected ? t('common.deselect-all') : t('common.select-all')}
              multiSelect={() => multiSelect(allSelected)}
            />
          )}

          {props.data.map((data: ListableRulesCardDataModel) => {
            // let name = data?.name === null && data?.name === undefined  ? "" : data.name;
            let id = data?.id === null && data?.id === undefined ? "" : data.id;

            return (
              <ListableRulesItem
                disabled={props.disabled}
                id={id}
                toolbarShowing={!props.disabled}
                modelType={props.modelType}
                fieldNameValue={data.fieldNameValue}
                handleFieldNameChange={(value: string, index: number) => { data.handleFieldNameChange(value, index) }}
                value={data.value}
                handleValueChange={data.handleValueChange}
                handleDelete={() => { props.handleDelete([`${data.id}`]) }}
                options={data.options !== null ? data.options : []}
                handleCheck={() => { handleCheck(id) }}
                selectedItems={selectedItems}
              />
            );
          })}
        </List>
      </Grid>
    );
  }

  return (
    <CustomCardContent>
      <SimpleCardHeader>
        <Box className={styles.cardHeader}>
          <Box className={styles.cardHeaderLeft}>
            <Box className={styles.cardTitle}>{props.title}</Box>
            {!props.isLoading && (
              <Chip label={props.data.length} variant="outlined" />
            )}
          </Box>
          {props.disabled ? null
            : props.actionButton.length < 2 ?
              <Box>
                <ButtonGroup>
                  {!props.isLoading &&
                    props.actionButton
                  }
                </ButtonGroup>
              </Box>
              :
              <Box>
                <Box className={styles.largeScreen}>
                  <ButtonGroup>
                    {!props.isLoading &&
                      props.actionButton
                    }
                  </ButtonGroup>
                </Box>
                <Box className={styles.smallScreen}>
                  <ShrinkingButtonGroup
                    secondary={(props.actionButton ?
                      props.actionButton.map((action: FixMeLater) => {
                        return (
                          {
                            label: action.props.children,
                            action: () => action.props.onClick()
                          }
                        )
                      }) :
                      [])}
                    default={{
                      label: "...",
                      action: null
                    }}
                  />
                </Box>
              </Box>
          }
        </Box>

      </SimpleCardHeader>
      <SimpleCardHeader>
        <Typography variant="body2">{props.subheading}</Typography>
      </SimpleCardHeader>

      {getDataList()}
    </CustomCardContent>
  );
}

export default memo(ListableRulesCard)
